import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Form, Modal, Button, Row, Col } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import { GIRVI_THEME } from "../girvi/girviTheme";
import Pagination from "../../components/Pagination";
import DhiranSlip from "../../components/DhiranSlip";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { createErpSocket } from "../../utils/socketIo";
import "../girvi/girvi.css";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../../hooks/useIsCurrentFinancialYear";
import {
  listReverseGirvis,
  showReverseGirvi,
  settlementReverseGirvi as postSettlementGirvi,
  listGirviBanks,
} from "../../erpClient";
import { formatIndianRupee } from "../../utils/formatLiveRate";
import {
  createGirviIdempotencyKey,
  isGirviApiSuccess,
  isReverseGirviClosed,
  mapReverseGirviSocketDetail,
  normalizeListReverseGirvisRows,
  reverseGirviStatusLabel,
  suggestedMonthlyInterest,
  validateReversePayout,
} from "../../utils/reverseGirviErpAdapter";
import { parseApiPagination } from "../../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../../utils/visitedPageCache";

function ReverseGirviTable() {
  const navigate = useNavigate();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();

  const canList = hasPermission(PERMISSIONS.REVERSE_GIRVI_VIEW);
  const canView = hasPermission(PERMISSIONS.REVERSE_GIRVI_VIEW);
  const canCreate = hasPermission(PERMISSIONS.REVERSE_GIRVI_ADD);
  const canEdit = hasPermission(PERMISSIONS.REVERSE_GIRVI_EDIT);
  const canSettlement = hasPermission(
    PERMISSIONS.REVERSE_GIRVI_QUICK_PAYMENT_VIEW,
  );
  const canBankDetails = hasPermission(PERMISSIONS.GIRVIBANKDETAILS);
  const [girviList, setGirviList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementGirvi, setSettlementGirvi] = useState(null);
  const [settlementGirviDetail, setSettlementGirviDetail] = useState(null);
  const [settlementDate, setSettlementDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const settlementIdempotencyKeyRef = useRef(null);
  const settlementPayloadFingerprintRef = useRef(null);

  // New Settlement Form States
  const [interestReceived, setInterestReceived] = useState("");
  const [principalReceived, setPrincipalReceived] = useState("");
  const [discount, setDiscount] = useState("");
  const [netAmount, setNetAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [bankType, setBankType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [intMonth, setIntMonth] = useState("");
  const [interestDays, setInterestDays] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [socket, setSocket] = useState(null);

  // Dhiranslip Modal States
  const [showDhiranSlip, setShowDhiranSlip] = useState(false);
  const [selectedGirviForSlip, setSelectedGirviForSlip] = useState(null);

  // Payment mode options for react-select
  const paymentModeOptions = [
    { value: "cash", label: "Cash" },
    { value: "cheque", label: "Cheque" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
  ];

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: buttonTableStyle === "square" ? "0" : "0.375rem",
      fontSize: "0.9rem",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10050,
    }),
    option: (base, state) => {
      const focused = Boolean(state.isFocused);
      const selected = Boolean(state.isSelected);
      let backgroundColor = "#ffffff";
      let color = "var(--bs-heading-color, #212529)";
      if (selected) {
        backgroundColor = "var(--theme-primary, #962a29)";
        color = "#ffffff";
      } else if (focused) {
        backgroundColor = "#DEEBFF";
        color = "#212529";
      }
      return {
        ...base,
        backgroundColor,
        color,
        fontWeight: selected ? 600 : 400,
        cursor: "pointer",
        ":active": {
          backgroundColor: selected
            ? "var(--theme-primary, #962a29)"
            : "#B2D4FF",
          color: selected ? "#ffffff" : "#212529",
        },
      };
    },
  };

  const selectMenuProps = {
    classNamePrefix: "girvi-select",
    styles: selectStyles,
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    onKeyDown: (event) => event.stopPropagation(),
  };

  const pageCache = useVisitedPageCache();

  const fetchGirviList = useCallback(async (force = false) => {
    const cacheKey = visitedPageKey([debouncedSearch, currentPage, itemsPerPage]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached) {
      setGirviList(cached.items);
      setTotalCount(cached.total);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await listReverseGirvis({
        search: debouncedSearch,
        page: currentPage,
        limit: itemsPerPage,
      });

      const normalized = normalizeListReverseGirvisRows(response);
      const total = parseApiPagination(response, normalized.length).total;
      setGirviList(normalized);
      setTotalCount(total);
      pageCache.write(cacheKey, { items: normalized, total });
    } catch (error) {
      console.error("Error fetching Reverse Girvi list:", error);
      toast.error("Failed to load Reverse Girvi deposits");
      setGirviList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, itemsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchGirviList();
  }, [fetchGirviList]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in ReverseGirviTable, refetching data...",
        event.detail,
      );
      setSelectedItems([]);
      setCurrentPage(1);
      fetchGirviList(true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [fetchGirviList]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("ReverseGirviTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("ReverseGirviTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("ReverseGirviTable: Socket connection error:", error);
    });

    newSocket.on("reverse_girvi_created", (data) => {
      const newGirvi = mapReverseGirviSocketDetail(data);
      if (!newGirvi?.id) return;

      // console.log("Processed new girvi data:", newGirvi);

      setGirviList((prev) => {
        const exists = prev.some(
          (girvi) => String(girvi.id) === String(newGirvi.id),
        );
        if (exists) {
          return prev.map((girvi) =>
            String(girvi.id) === String(newGirvi.id) ? newGirvi : girvi,
          );
        }
        return [newGirvi, ...prev];
      });
    });

    newSocket.on("reverse_girvi_updated", (data) => {
      const updatedGirvi = mapReverseGirviSocketDetail(data);
      if (!updatedGirvi?.id) return;

      // console.log("Processed updated girvi data:", updatedGirvi);

      setGirviList((prev) =>
        prev.map((girvi) =>
          String(girvi.id) === String(updatedGirvi.id) ? updatedGirvi : girvi,
        ),
      );

      toast.success("Reverse Girvi updated");
    });

    return () => {
      newSocket.off("reverse_girvi_created");
      newSocket.off("reverse_girvi_updated");
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    const interest = parseFloat(interestReceived) || 0;
    const principal = parseFloat(principalReceived) || 0;
    const disc = parseFloat(discount) || 0;
    setNetAmount((interest + principal - disc).toFixed(2));
  }, [interestReceived, principalReceived, discount]);

  // Auto-select cash bank when modal opens and payment mode is cash
  useEffect(() => {
    if (
      showSettlementModal &&
      payMode === "cash" &&
      paymentTypes.length > 0 &&
      !bankType
    ) {
      const cashBank = paymentTypes.find((bank) =>
        bank.label.toLowerCase().includes("cash"),
      );
      if (cashBank) {
        setBankType(cashBank.value);
      }
    }
  }, [showSettlementModal, payMode, paymentTypes, bankType]);

  const fetchBanks = async () => {
    try {
      const response = await listGirviBanks({});
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.girvi_banks)
          ? response.data.girvi_banks
          : [];

      if (isGirviApiSuccess(response) && rows.length >= 0) {
        setPaymentTypes(
          rows.map((bank) => ({
            value: String(bank.id),
            label:
              bank.bank_name ||
              bank.name ||
              bank.account_type ||
              `Bank ${bank.id}`,
          })),
        );
      } else {
        setPaymentTypes([]);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      setPaymentTypes([]);
    }
  };

  // Status filter is client-side on the current server page; search is server-side.
  const filteredGirviList = girviList.filter((item) => {
    const itemStatus = reverseGirviStatusLabel(item);

    return (
      statusFilter === "All" ||
      (statusFilter === "Closed" && itemStatus === "Closed") ||
      (statusFilter === "Active" && itemStatus === "Active")
    );
  });

  const displayedGirviList = filteredGirviList;

  // Pagination logic
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const prefillSettlement = (girvi, today) => {
    const start = String(girvi?.girvi_date || girvi?.reverse_girvi_date || today).slice(0, 10);
    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${today}T00:00:00`);
    const diffDays = Number.isNaN(startDate.getTime())
      ? 0
      : Math.max(0, Math.round((endDate - startDate) / 86400000));
    const months = Math.floor(diffDays / 30);
    const suggested = suggestedMonthlyInterest(
      girvi?.remaining_amount,
      girvi?.interest_percent,
    );
    setFromDate(start || today);
    setToDate(today);
    setIntMonth(String(months));
    setInterestDays(String(diffDays));
    setInterestReceived(suggested > 0 ? suggested.toFixed(2) : "");
    setPrincipalReceived("");
  };

  const openSettlementModal = async (girvi) => {
    if (isReverseGirviClosed(girvi) || reverseGirviStatusLabel(girvi) === "Closed") {
      toast.error("This deposit is closed");
      return;
    }
    try {
      setSettlementLoading(true);
      setSettlementGirvi(girvi);
      setShowSettlementModal(true);
      const today = new Date().toISOString().split("T")[0];
      setSettlementDate(today);

      setDiscount("");
      setRemarks("");
      setNetAmount(0);
      setPayMode("cash");
      setBankType("");
      prefillSettlement(girvi, today);

      // Fetch banks only when opening the modal, if not already fetched
      if (paymentTypes.length === 0) {
        fetchBanks();
      }
      setSettlementGirviDetail(null);
    } catch (e) {
      setSettlementGirviDetail(null);
    } finally {
      setSettlementLoading(false);
    }
  };

  const closeSettlementModal = () => {
    setShowSettlementModal(false);
    setSettlementGirvi(null);
    setSettlementGirviDetail(null);

    settlementIdempotencyKeyRef.current = null;
    settlementPayloadFingerprintRef.current = null;

    setInterestReceived("");
    setPrincipalReceived("");
    setDiscount("");
    setRemarks("");
    setNetAmount(0);
    setPayMode("cash");
    setBankType("");
    setFromDate("");
    setToDate("");
    setIntMonth("");
    setInterestDays("");
  };

  const fillSettlementInterest = () => {
    const suggested = suggestedMonthlyInterest(
      settlementGirvi?.remaining_amount,
      settlementGirvi?.interest_percent,
    );
    setInterestReceived(suggested > 0 ? suggested.toFixed(2) : "");
    setPrincipalReceived("");
  };

  const fillSettlementPrincipal = () => {
    const remaining = parseFloat(settlementGirvi?.remaining_amount || 0) || 0;
    setPrincipalReceived(remaining > 0 ? remaining.toFixed(2) : "");
    setInterestReceived("");
  };

  const fillSettlementClose = () => {
    const remaining = parseFloat(settlementGirvi?.remaining_amount || 0) || 0;
    const suggested = suggestedMonthlyInterest(
      settlementGirvi?.remaining_amount,
      settlementGirvi?.interest_percent,
    );
    setPrincipalReceived(remaining > 0 ? remaining.toFixed(2) : "");
    setInterestReceived(suggested > 0 ? suggested.toFixed(2) : "");
  };

  const handleSettlementSubmit = async (e) => {
    e.preventDefault();
    if (!settlementGirvi?.id) {
      toast.error("Reverse Girvi is not selected");
      return;
    }
    if (!canSettlement) {
      toast.error("Not authorized to pay the customer");
      return;
    }

    const principalRecv = parseFloat(principalReceived) || 0;
    const interestRecv = parseFloat(interestReceived) || 0;
    const bankId = bankType ? parseInt(bankType, 10) : null;
    const payModeValue = payMode;
    const payoutError = validateReversePayout({
      status: settlementGirvi.status,
      remaining: settlementGirvi.remaining_amount,
      principalPaid: principalRecv,
      interestPaid: interestRecv,
    });
    if (payoutError) {
      toast.error(payoutError);
      return;
    }

    if (payModeValue !== "cash" && bankId == null) {
      toast.error("Please select a bank");
      return;
    }

    // Idempotency (stable per action payload)
    const basePayload = {
      reverse_girvi_id: settlementGirvi.id,
      principal_paid: principalRecv,
      interest_paid: interestRecv,
      bank_type: bankId,
      pay_mode: payModeValue,
      remarks: remarks || "",
      payment_date: settlementDate,
    };
    const fingerprint = JSON.stringify(basePayload);
    if (
      !settlementIdempotencyKeyRef.current ||
      settlementPayloadFingerprintRef.current !== fingerprint
    ) {
      settlementIdempotencyKeyRef.current = createGirviIdempotencyKey();
      settlementPayloadFingerprintRef.current = fingerprint;
    }

    try {
      const response = await postSettlementGirvi({
        idempotency_key: settlementIdempotencyKeyRef.current,
        reverse_girvi_id: settlementGirvi.id,
        payment: {
          principal_paid: principalRecv,
          interest_paid: interestRecv,
          bank_type: bankId ?? 0,
          pay_mode: payModeValue,
          remarks: remarks || "",
          payment_date: settlementDate,
          from_date: fromDate || null,
          to_date: toDate || null,
          int_month: intMonth === "" ? 0 : parseInt(intMonth, 10) || 0,
          interest_days: interestDays === "" ? 0 : parseInt(interestDays, 10) || 0,
        },
      });

      if (isGirviApiSuccess(response)) {
        toast.success("Payment paid to customer");
        // Always refresh by calling show()
        try {
          await showReverseGirvi({ id: settlementGirvi.id });
        } catch {
          // ignore; list refresh below will handle UI
        }
        closeSettlementModal();
        fetchGirviList(true);
      } else {
        toast.error(response?.message || "Failed to record payment");
      }
    } catch (error) {
      console.error("Error recording Reverse Girvi payout:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to record payment",
      );
    }
  };

  // Reset page if it becomes invalid after server total changes
  useEffect(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    if (totalCount === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalCount, itemsPerPage, currentPage]);

  // Handle checkbox selection
  const handleCheckboxChange = (itemId) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        return [...prevSelected, itemId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedItems.length === displayedGirviList.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(displayedGirviList.map((item) => item.id));
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass} ${
            buttonTableStyle === "square" ? "rounded-0" : ""
          }`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Reverse Girvi</h4>
            {canCreate && (
              <button
                className={`btn btn-success ${buttonClass}`}
                disabled={!isCurrentFinancialYear}
                title={
                  !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                }
                onClick={() => {
                  if (!isCurrentFinancialYear) return;
                  navigate("/addreversegirvi");
                }}
              >
                Create Reverse Girvi / Deposit
              </button>
            )}
          </div>

          <div className="card-body p-4">
            {/* Search Section */}
            <div className="d-flex align-items-center mb-3">
              {/* Left side: Search Input */}
              <input
                type="text"
                className="form-control"
                style={{
                  maxWidth: "260px",
                  borderRadius: "8px",
                  borderColor: GIRVI_THEME.borderColor,
                }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />

              {/* Right side: Status Filter */}
              <div className="ms-auto d-flex align-items-center gap-2">
                <select
                  className="form-select w-auto"
                  style={{
                    maxWidth: "200px",
                    borderRadius: "8px",
                    borderColor: GIRVI_THEME.borderColor,
                  }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="All">All Status</option>
                  <option value="Closed">Closed</option>
                  <option value="Active">Active</option>
                </select>
              </div>
            </div>

            <div
              className={`table-responsive mb-4 border ${cardClass} ${
                buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
            >
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass} ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Number</th>
                    <th>Customer</th>
                    <th>Principal Taken</th>
                    <th>Remaining</th>
                    <th>Interest %</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedGirviList.length > 0 ? (
                    displayedGirviList.map((girvi) => {
                      const currentStatus = reverseGirviStatusLabel(girvi);
                      const dueLabel = girvi.due_date
                        ? String(girvi.due_date).slice(0, 10)
                        : "—";

                      // Determine row class based on status
                      const rowClassName =
                        currentStatus === "Closed"
                          ? "status-closed-row"
                          : "status-active-row";

                      return (
                        <tr key={girvi.id} className={rowClassName}>
                          <td>{girvi.girvi_number}</td>
                          <td>
                            <div>{girvi.customer_name || "-"}</div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "0.86rem" }}
                            >
                              ({girvi.customer_mobile || "-"})
                            </div>
                          </td>
                          <td>
                            {formatIndianRupee(
                              girvi.principal_taken || girvi.principal_given || 0,
                            )}
                          </td>
                          <td>
                            {formatIndianRupee(girvi.remaining_amount || 0)}
                          </td>
                          <td>{girvi.interest_percent || 0}%</td>
                          <td>{dueLabel}</td>
                          <td>
                            <span
                              className={`badge ${
                                currentStatus === "Active"
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                              }`}
                            >
                              {currentStatus}
                            </span>
                          </td>
                          <td>
                            {canSettlement && (
                              <button
                                className={`btn btn-sm me-2 ${buttonClass}`}
                                onClick={() => openSettlementModal(girvi)}
                                title="Pay to Customer"
                                disabled={
                                  !canSettlement || currentStatus === "Closed"
                                }
                                style={{
                                  backgroundColor: GIRVI_THEME.bgColor,
                                  color: GIRVI_THEME.textColor,
                                  borderColor: GIRVI_THEME.bgColor,
                                }}
                              >
                                <Icon
                                  icon="solar:wallet-money-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                            {(canView || canEdit) && (
                              <button
                                className={`btn btn-sm me-2 ${buttonClass}`}
                                onClick={() => {
                                  navigate("/editreversegirvi", {
                                    state: {
                                      girviId: girvi.id,
                                      viewOnly: true,
                                    },
                                  });
                                }}
                                title="View Reverse Girvi"
                                style={{
                                  backgroundColor: GIRVI_THEME.bgColor,
                                  color: GIRVI_THEME.textColor,
                                  borderColor: GIRVI_THEME.bgColor,
                                }}
                              >
                                <Icon
                                  icon="solar:eye-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                            {canEdit && (
                              <button
                                className={`btn btn-sm ${buttonClass}`}
                                onClick={() => {
                                  navigate("/editreversegirvi", {
                                    state: { girviId: girvi.id },
                                  });
                                }}
                                title="Edit Reverse Girvi"
                                style={{
                                  backgroundColor: GIRVI_THEME.bgColor,
                                  color: GIRVI_THEME.textColor,
                                  borderColor: GIRVI_THEME.bgColor,
                                }}
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                            <button
                              className="btn btn-sm ms-2"
                              onClick={() => {
                                setSelectedGirviForSlip(girvi);
                                setShowDhiranSlip(true);
                              }}
                              title="Print"
                              style={{
                                backgroundColor: GIRVI_THEME.bgColor,
                                color: GIRVI_THEME.textColor,
                                borderColor: GIRVI_THEME.bgColor,
                                borderRadius: "8px",
                              }}
                            >
                              <Icon
                                icon="solar:printer-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No Reverse Girvi deposits found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={
                statusFilter === "All" ? totalCount : filteredGirviList.length
              }
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>
      {/* Settlement Modal */}
      <Modal
        show={showSettlementModal}
        onHide={closeSettlementModal}
        centered
        enforceFocus={false}
        contentClassName={cardClass}
        dialogClassName="custom-modal-width"
        style={{
          "--bs-modal-width": "600px",
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            Pay to Customer
            {settlementGirvi?.customer_name && (
              <span
                className="badge bg-secondary"
                style={{ fontSize: "0.75rem" }}
              >
                {settlementGirvi.customer_name}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {settlementLoading ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <div
                className="spinner-border"
                style={{ color: GIRVI_THEME.bgColor }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Info Header: Principal Taken | Remaining liability | Monthly Interest */}
              <Row className="g-3 mb-3">
                <Col md={3}>
                  <label className="form-label small  fw-semibold">
                    Principal Taken
                  </label>
                  <div className="form-control bg-light">
                    {formatIndianRupee(settlementGirvi?.principal_given || 0)}
                  </div>
                </Col>
                <Col md={3}>
                  <label className="form-label small fw-semibold">
                    Remaining liability
                  </label>
                  <div className="form-control bg-light">
                    {formatIndianRupee(
                      Math.max(
                        0,
                        (parseFloat(settlementGirvi?.remaining_amount || 0) ||
                          0) - (parseFloat(principalReceived) || 0),
                      )
                    )}
                  </div>
                </Col>
              </Row>

              <Form onSubmit={handleSettlementSubmit}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="fw-semibold">From date</Form.Label>
                    <Form.Control
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="fw-semibold">To date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </Col>
                  {/* Interest Paid */}
                  <Col xs={12}>
                    <Form.Label className="fw-semibold ">
                      Interest Paid
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={interestReceived}
                      onChange={(e) => setInterestReceived(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className={
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      }
                    />
                  </Col>

                  {/* Principal Paid */}
                  <Col xs={12}>
                    <Form.Label className="fw-semibold ">
                      Principal Paid
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={principalReceived}
                      onChange={(e) => setPrincipalReceived(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className={
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      }
                    />
                  </Col>

                  {/* Payment Date */}
                  <Col xs={12}>
                    <Form.Label className="fw-semibold ">
                      Payment Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={settlementDate}
                      onChange={(e) => setSettlementDate(e.target.value)}
                      className={
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      }
                    />
                  </Col>

                  {/* Remark */}
                  <Col xs={12}>
                    <Form.Label className="fw-semibold ">Remark</Form.Label>
                    <Form.Control
                      type="text"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks..."
                      className={
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      }
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label className="fw-semibold ">
                      Payment Mode
                    </Form.Label>
                    <Select
                      value={
                        paymentModeOptions.find(
                          (option) => option.value === payMode,
                        ) || null
                      }
                      onChange={(selectedOption) => {
                        const mode = selectedOption ? selectedOption.value : "";
                        setPayMode(mode);
                        if (mode === "cash") {
                          // Auto-select Cash bank if available
                          const cashBank = paymentTypes.find((bank) =>
                            bank.label.toLowerCase().includes("cash"),
                          );
                          if (cashBank) {
                            setBankType(cashBank.value);
                          } else {
                            setBankType("");
                          }
                        } else {
                          setBankType("");
                        }
                      }}
                      options={paymentModeOptions}
                      placeholder="Select Payment Mode"
                      isClearable={false}
                      className={
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      }
                      {...selectMenuProps}
                    />
                  </Col>

                  {(payMode === "cash" ||
                    payMode === "cheque" ||
                    payMode === "bank_transfer" ||
                    payMode === "upi" ||
                    payMode === "card") && (
                    <Col md={6}>
                      <Form.Label className="fw-semibold text-muted">
                        Bank
                      </Form.Label>
                      <Select
                        value={
                          paymentTypes.find(
                            (bank) => bank.value === bankType,
                          ) || null
                        }
                        onChange={(selectedOption) => {
                          setBankType(
                            selectedOption ? selectedOption.value : "",
                          );
                        }}
                        options={paymentTypes}
                        placeholder="Select Bank"
                        isClearable={true}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                        {...selectMenuProps}
                      />
                    </Col>
                  )}
                </Row>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-secondary"
            className={buttonClass}
            type="button"
            onClick={fillSettlementInterest}
          >
            Pay Interest
          </Button>
          <Button
            variant="outline-secondary"
            className={buttonClass}
            type="button"
            onClick={fillSettlementPrincipal}
          >
            Pay Principal
          </Button>
          <Button
            variant="outline-dark"
            className={buttonClass}
            type="button"
            onClick={fillSettlementClose}
          >
            Close & Pay All
          </Button>
          <Button
            variant="success"
            className={buttonClass}
            onClick={handleSettlementSubmit}
            type="submit"
            disabled={!canSettlement}
          >
            Pay to Customer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Dhiranslip Modal Component */}
      <DhiranSlip
        show={showDhiranSlip}
        onHide={() => {
          setShowDhiranSlip(false);
          setSelectedGirviForSlip(null);
        }}
        girviData={selectedGirviForSlip}
        fetchDocument={showReverseGirvi}
        title="Reverse Girvi"
        variant="reverse"
      />
    </div>
  );
}

export default ReverseGirviTable;
